import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  consumeVolunteerMagicLink,
  extractApiError,
  fetchVolunteerMe,
  requestVolunteerLink,
} from '../api.js';
import { takeAuthNext } from '../components/StaffEmailLogin.jsx';
import {
  getVolunteerSession,
  saveVolunteerSession,
  updateVolunteerMetaFromMe,
} from '../useActiveRoles.js';

function redirectAfterLogin(data) {
  const next = takeAuthNext();
  const canAdmin = data.is_super_admin || data.staff_role === 'admin';
  const canSaisie = canAdmin || data.staff_role === 'saisie';
  // Ne redirige vers admin/caisse que si la demande de lien venait de ces pages.
  if (next === '/admin' && canAdmin) return '/admin';
  if (next === '/saisie' && canSaisie) return '/saisie';
  return '/benevoles/planning';
}

export default function ConnexionBenevole() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSending, setLinkSending] = useState(false);
  const [linkMsg, setLinkMsg] = useState(null);
  const [linkError, setLinkError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token?.trim()) {
        setError('Lien invalide.');
        return;
      }
      try {
        const data = await consumeVolunteerMagicLink(token.trim());
        if (cancelled) return;
        saveVolunteerSession({
          sessionToken: data.session_token,
          volunteer: data.volunteer,
          staffRole: data.staff_role,
          isSuperAdmin: data.is_super_admin,
        });
        navigate(redirectAfterLogin(data), { replace: true });
      } catch (err) {
        const existing = getVolunteerSession();
        if (existing) {
          try {
            const me = await fetchVolunteerMe(existing);
            if (!cancelled && me?.volunteer) {
              updateVolunteerMetaFromMe(me);
              navigate(
                redirectAfterLogin({
                  is_super_admin: me.is_super_admin,
                  staff_role: me.staff_role,
                }),
                { replace: true },
              );
              return;
            }
          } catch {
            /* session locale périmée : on affiche l’erreur du lien */
          }
        }
        if (!cancelled) {
          setError(
            extractApiError(
              err,
              'Lien invalide ou expiré. Demandez un nouveau lien depuis la page Bénévoles.',
            ),
          );
        }
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  async function handleRequestLink(e) {
    e.preventDefault();
    const email = linkEmail.trim();
    if (!email) return;
    setLinkSending(true);
    setLinkError(null);
    setLinkMsg(null);
    try {
      const data = await requestVolunteerLink(email);
      setLinkMsg(data.message || 'Si un compte validé existe, un lien a été envoyé.');
    } catch (err) {
      setLinkError(extractApiError(err, 'Envoi impossible.'));
    } finally {
      setLinkSending(false);
    }
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider', mt: 2 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h5">Connexion bénévole</Typography>
          {!error && (
            <>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">
                Validation du lien…
              </Typography>
            </>
          )}
          {error && (
            <>
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                Votre compte peut toujours être validé. Indiquez votre email pour recevoir
                un nouveau lien.
              </Typography>
              <Stack
                component="form"
                onSubmit={handleRequestLink}
                spacing={1.5}
                sx={{ width: '100%' }}
              >
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                />
                {linkError && <Alert severity="error">{linkError}</Alert>}
                {linkMsg && <Alert severity="success">{linkMsg}</Alert>}
                <Button type="submit" variant="contained" disabled={linkSending}>
                  {linkSending ? 'Envoi…' : 'Recevoir un nouveau lien'}
                </Button>
              </Stack>
              <Typography
                component={RouterLink}
                to="/benevoles"
                variant="body2"
                sx={{ color: 'primary.main' }}
              >
                Retour à la page Bénévoles
              </Typography>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
